import React, { Component } from 'react'
import { createClient } from '@supabase/supabase-js'
import sampleHealth from '../data/automationHealth.sample.json'

// At-a-glance only: name, last run, health dot. No lastResult/nextRun or
// other internal details are rendered. See MANUAL_TODOS.md for how to swap
// the bundled sample data for real health JSON.
const STATUS_COLORS = {
  healthy: '#2ecc71',
  warn: '#f1c40f',
  error: '#e74c3c',
}

function formatLastRun(iso) {
  const d = new Date(iso)
  return isNaN(d) ? iso : d.toLocaleString()
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY
const SUPABASE_ENABLED = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)

// Row shape from the `automation_health` table -> the { name, status, lastRun }
// shape the rest of this component already renders.
function mapRow(row) {
  return { name: row.name, status: row.status, lastRun: row.last_run }
}

// Upsert a single realtime row into the current list, keyed by name.
function applyChange(reports, payload) {
  if (payload.eventType === 'DELETE') {
    const deletedName = payload.old && payload.old.name
    return reports.filter((r) => r.name !== deletedName)
  }
  const updated = mapRow(payload.new)
  const idx = reports.findIndex((r) => r.name === updated.name)
  if (idx === -1) return [...reports, updated]
  const next = reports.slice()
  next[idx] = updated
  return next
}

export default class AutomationStatus extends Component {
  constructor(props) {
    super(props)
    this.state = { reports: props.data || sampleHealth }
    this.supabase = null
    this.channel = null
  }

  componentDidMount() {
    // Explicit data prop (e.g. tests) or missing Supabase config: keep the
    // bundled sample / passed-in data, never touch the network.
    if (this.props.data || !SUPABASE_ENABLED) return

    this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

    // Initial load: one SELECT to populate current state.
    this.supabase
      .from('automation_health_public')
      .select('name, status, last_run')
      .then(({ data, error }) => {
        if (!error && data && data.length) {
          this.setState({ reports: data.map(mapRow) })
        }
      })

    // Then subscribe for live updates.
    this.channel = this.supabase
      .channel('automation_health_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'automation_health_public' },
        (payload) => {
          this.setState((prev) => ({ reports: applyChange(prev.reports, payload) }))
        }
      )
      .subscribe()
  }

  componentWillUnmount() {
    if (this.channel && this.supabase) {
      this.supabase.removeChannel(this.channel)
    }
  }

  render() {
    const reports = this.state.reports

    return (
      <section className="colorlib-automation-status" data-section="automationStatus">
        <div className="colorlib-narrow-content">
          <div className="row">
            <div className="col-md-6 col-md-offset-3 col-md-pull-3 animate-box" data-animate-effect="fadeInLeft">
              <span className="heading-meta">At a Glance</span>
              <h2 className="colorlib-heading">Automation Status</h2>
            </div>
          </div>
          <div className="row row-pt-md">
            <div className="col-md-8 col-md-offset-2">
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {reports.map((r) => (
                  <li key={r.name} style={{ display: 'flex', alignItems: 'center', padding: '0.4rem 0' }}>
                    <span
                      title={r.status}
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS[r.status] || '#999',
                        marginRight: '0.75rem',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontWeight: 600, marginRight: '0.75rem' }}>{r.name}</span>
                    <span style={{ opacity: 0.75 }}>last run: {formatLastRun(r.lastRun)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    )
  }
}
